/**
 * NuShell — the full side-rail shell frame, ported from
 * NU-DATA-DASHBOARD/kits/_core/shell.jsx (NuShell, side layout) composed the
 * way kits/nu-data/components/app.jsx actually mounts it: NuShell frame +
 * NuTopActionBar, with the NU DATA right user panel (NuUserPanel, the kit's
 * NuDataRightRail) as a fixed sibling — the content gutter comes from the
 * body[data-nu-right-slider] rules in src/styles/nu/nu-brand.css, exactly as
 * in the kit.
 *
 * Layout contract (nu-tokens.css :has rules): the frame is the viewport; the
 * rail and the 52px toolbar are fixed; ONLY the content column scrolls.
 *
 * The kit's top-toolbar layout variant (shell-top.jsx) and the
 * localStorage['nu-system-layout'] switch are NOT ported in this slice —
 * side-rail only (brief 00 follow-up).
 */

import React, { type ReactNode } from "react";
import { NuShellRail, type ShellTheme } from "./NuShellRail";
import { NuTopActionBar } from "./NuTopActionBar";
import { NuUserPanel, type ShellUser } from "./NuUserPanel";
import type { NavigateFn, ShellNavGroup, ShellNavItem } from "./navConfig";

export interface NuShellProps {
  /** Current SPA path (location.pathname). */
  route: string;
  onNavigate: NavigateFn;
  theme: ShellTheme;
  onToggleTheme?: () => void;
  /** Pre-filtered nav (visibleNavForRole). */
  navGroups: readonly ShellNavGroup[];
  navFooter: readonly ShellNavItem[];
  /** Toolbar page title. */
  title?: string;
  /**
   * [#663] Optional toolbar identity slot (see NuTopActionBar.identity).
   * Additive — untouched screens render exactly as before.
   */
  identity?: ReactNode;
  user?: ShellUser;
  /** Real sign-out through the auth/tokenStore seam. */
  onLogout?: () => void | Promise<void>;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  /** Opt out of the top action bar (kit `actionBar={false}`). */
  actionBar?: boolean;
  /** Opt out of the right user panel (kit `tools={false}`). */
  tools?: boolean;
  children?: ReactNode;
}

export function NuShell({
  route,
  onNavigate,
  theme,
  onToggleTheme,
  navGroups,
  navFooter,
  title,
  identity,
  user,
  onLogout,
  mobileOpen,
  onCloseMobile,
  actionBar = true,
  tools = true,
  children,
}: NuShellProps) {
  return (
    <div className="nu-frame">
      <div className="nu-frame__bg" aria-hidden="true" />
      <div className="nu-frame__inner">
        <NuShellRail
          route={route}
          onNavigate={onNavigate}
          theme={theme}
          onToggleTheme={onToggleTheme}
          mobileOpen={mobileOpen}
          onCloseMobile={onCloseMobile}
          navGroups={navGroups}
          navFooter={navFooter}
        />
        {mobileOpen && (
          <div className="nu-drawer-scrim" onClick={onCloseMobile} />
        )}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {actionBar && <NuTopActionBar title={title} identity={identity} />}
          <div className="nu-content">
            <div className="nu-content__main">{children}</div>
          </div>
        </div>
      </div>

      {tools && (
        <NuUserPanel
          theme={theme}
          onToggleTheme={onToggleTheme}
          onNavigate={onNavigate}
          onLogout={onLogout}
          user={user}
        />
      )}
    </div>
  );
}

export default NuShell;
